import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { useHasQueueMetrics } from '@/api/use-branch-features';
import { reloginOnQueues } from '@/api/use-queue-login';
import { defaultPauseReasons } from '@/config/default-pause-reasons';
import { env } from '@/config/env';
import { reactQueryKeys } from '@/config/react-query';
import { useCodWeb } from '@/hooks/use-codweb';
import { useExtension } from '@/hooks/use-extension';
import { fetchQueueRequest } from '@/lib/fetch-queue-request';
import { getApiHeaders } from '@/lib/getApiHeaders';
import {
  HttpError,
  apiErrorCodes,
  queueRetryDelay,
  shouldRetryQueueRequest,
} from '@/lib/http-error';

type GetPauseStatusParams = {
  codWeb: string;
  extension: string;
};

const GetPauseStatusResponseSchema = z.object({
  code: z
    .union([z.string().min(1), z.number().int()])
    .transform(String)
    .nullable(),
  date: z.coerce.date().nullable(),
});

const getPauseStatus = async (params: GetPauseStatusParams) => {
  const route = `${env.VITE_BACKEND_URL}/api/${params.codWeb}/queues/extensions/${params.extension}/pause-status`;
  const headers = await getApiHeaders();

  return fetchQueueRequest(route, { headers }, async (response) => {
    if (!response.ok) throw await HttpError.fromResponse(response);
    return GetPauseStatusResponseSchema.parse(await response.json());
  });
};

export const usePauseStatus = () => {
  const codWeb = useCodWeb();
  const hasQueueMetrics = useHasQueueMetrics();
  const extension = useExtension();

  return useQuery({
    queryKey: [
      reactQueryKeys.pauseStatus,
      String(codWeb),
      extension?.number || '',
    ],
    queryFn: () =>
      getPauseStatus({ codWeb, extension: extension?.number || '' }),
    enabled: Boolean(codWeb && extension && hasQueueMetrics === true),
    retry: shouldRetryQueueRequest,
    retryOnMount: false,
    refetchOnWindowFocus: (query) => query.state.status !== 'error',
    retryDelay: queueRetryDelay,
  });
};

type GetPauseReasonsParams = {
  codWeb: string;
  extension: string;
};

const PauseReasonSchema = z.object({
  pauseReasonName: z.string().min(1),
  pauseReasonCode: z.coerce.string().min(1),
});
type PauseReason = z.infer<typeof PauseReasonSchema>;

const GetPauseReasonsResponseSchema = z.object({
  pauseReasons: z.array(PauseReasonSchema),
});

const defaultPauseReason = {
  duration: 10,
  pauseReasonCode: defaultPauseReasons.LOGIN,
  pauseReasonName: 'default',
};

const getPauseDuration = (pauseName: string) => {
  const regex = /(\d+)\s*minutos/i;
  const result = pauseName.match(regex);
  const duration = result?.[1];

  return duration ? parseInt(duration, 10) : 10;
};

const addDurationToPauseReason = (pauseReason: PauseReason) => ({
  ...pauseReason,
  duration: getPauseDuration(pauseReason.pauseReasonName),
});

const getPauseReasons = async (params: GetPauseReasonsParams) => {
  const route = `${env.VITE_BACKEND_URL}/api/${params.codWeb}/queues/extensions/${params.extension}/pause-reasons`;
  const headers = await getApiHeaders();

  return fetchQueueRequest(route, { headers }, async (response) => {
    if (!response.ok) throw await HttpError.fromResponse(response);
    const { pauseReasons } = GetPauseReasonsResponseSchema.parse(
      await response.json()
    );
    return pauseReasons.map(addDurationToPauseReason);
  });
};

export const usePauseReasons = () => {
  const codWeb = useCodWeb();
  const hasQueueMetrics = useHasQueueMetrics();
  const extension = useExtension();

  return useQuery({
    queryKey: [
      reactQueryKeys.pauseReasons,
      String(codWeb),
      extension?.number || '',
    ],
    queryFn: () =>
      getPauseReasons({ codWeb, extension: extension?.number || '' }),
    enabled: Boolean(codWeb && extension && hasQueueMetrics === true),
    retry: shouldRetryQueueRequest,
    retryOnMount: false,
    refetchOnWindowFocus: (query) => query.state.status !== 'error',
    retryDelay: queueRetryDelay,
    initialData: [],
    select: (data) => [...data, defaultPauseReason],
  });
};

// Asterisk may drop the agent from its queues (e.g. after a softphone crash) and
// the backend has already cleared its state, so log in again and retry once.
// Login leaves the agent in the "login" pause, so the retry is also required for unpause.
const withQueueRelogin = async (
  params: { codWeb: string; extension: string },
  request: () => Promise<void>
) => {
  try {
    await request();
  } catch (error) {
    if (
      !(error instanceof HttpError) ||
      error.code !== apiErrorCodes.AGENT_NOT_LOGGED_IN
    ) {
      throw error;
    }
    await reloginOnQueues(params);
    await request();
  }
};

type PauseParams = {
  codWeb: string;
  extension: string;
  reasonCode: string;
};

const pause = async (params: PauseParams) => {
  const route = `${env.VITE_BACKEND_URL}/api/${params.codWeb}/queues/extensions/${params.extension}/pause`;
  const headers = {
    'Content-Type': 'application/json',
    ...(await getApiHeaders()),
  };
  const body = { pauseReasonCode: params.reasonCode };

  await fetchQueueRequest(
    route,
    { method: 'PATCH', headers, body: JSON.stringify(body) },
    async (response) => {
      if (!response.ok) throw await HttpError.fromResponse(response);
    }
  );
};

export const usePause = () => {
  const codWeb = useCodWeb();
  const hasQueueMetrics = useHasQueueMetrics();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      extension,
      reasonCode,
    }: {
      extension: string;
      reasonCode: string;
    }) => {
      if (hasQueueMetrics !== true)
        throw new HttpError(409, apiErrorCodes.QUEUEMETRICS_NOT_CONFIGURED);
      return withQueueRelogin({ codWeb, extension }, () =>
        pause({
          codWeb,
          extension,
          reasonCode,
        })
      );
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [reactQueryKeys.pauseStatus],
      }),
  });
};

type UnpauseParams = { codWeb: string; extension: string };

const unpause = async (params: UnpauseParams) => {
  const route = `${env.VITE_BACKEND_URL}/api/${params.codWeb}/queues/extensions/${params.extension}/unpause`;
  const headers = {
    'Content-Type': 'application/json',
    ...(await getApiHeaders()),
  };

  await fetchQueueRequest(
    route,
    { method: 'PATCH', headers },
    async (response) => {
      if (!response.ok) throw await HttpError.fromResponse(response);
    }
  );
};

export const useUnpause = () => {
  const codWeb = useCodWeb();
  const hasQueueMetrics = useHasQueueMetrics();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (extension: string) => {
      if (hasQueueMetrics !== true)
        throw new HttpError(409, apiErrorCodes.QUEUEMETRICS_NOT_CONFIGURED);
      return withQueueRelogin({ codWeb, extension }, () =>
        unpause({
          codWeb,
          extension,
        })
      );
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [reactQueryKeys.pauseStatus],
      }),
  });
};
